/**
 * Section Editor Component
 * Dialog for creating/editing prompt sections
 */

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { PromptSection, SectionType } from '@/types/promptTemplates';
import { SECTION_TYPE_INFO, AVAILABLE_SOURCES } from '@/types/promptTemplates';

interface SectionEditorProps {
  isOpen: boolean;
  section: PromptSection | null;
  onClose: () => void;
  onSave: (section: PromptSection) => void;
  isSubmitting?: boolean;
}

const SECTION_TYPES: SectionType[] = ['static', 'hook', 'dependency', 'runtime'];

const DEFAULT_SECTION: PromptSection = {
  id: '',
  name: '',
  type: 'static',
  order: 50,
  enabled: true,
  source: 'config.description', // Default to first static source
  wrap_tag: null,
  template: null,
  description: null,
};

export function SectionEditor({
  isOpen,
  section,
  onClose,
  onSave,
  isSubmitting,
}: SectionEditorProps) {
  const [formData, setFormData] = useState<PromptSection>(DEFAULT_SECTION);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = section !== null;

  useEffect(() => {
    if (section) {
      setFormData(section);
    } else {
      setFormData(DEFAULT_SECTION);
    }
    setErrors({});
  }, [section, isOpen]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.id.trim()) {
      newErrors.id = 'ID is required';
    } else if (!/^[a-z_][a-z0-9_]*$/.test(formData.id)) {
      newErrors.id = 'ID must be lowercase with underscores only';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.source.trim()) {
      newErrors.source = 'Source is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  const handleChange = (field: keyof PromptSection, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // When type changes, reset source to first available option
      if (field === 'type') {
        const newType = value as SectionType;
        const sources = AVAILABLE_SOURCES[newType];
        if (sources && sources.length > 0) {
          updated.source = sources[0].value;
        } else {
          updated.source = '';
        }
      }

      // When source changes, auto-fill ID and name if empty
      if (field === 'source' && !isEditing) {
        const sourceValue = value as string;
        const [, key] = sourceValue.split('.');
        if (key) {
          if (!prev.id) {
            updated.id = key;
          }
          if (!prev.name) {
            // Find the label from available sources
            const allSources = Object.values(AVAILABLE_SOURCES).flat();
            const sourceOption = allSources.find(s => s.value === sourceValue);
            updated.name = sourceOption?.label || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          }
        }
      }

      return updated;
    });

    if (errors[field]) {
      setErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Section' : 'Add Section'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Section ID *
            </label>
            <input
              type="text"
              value={formData.id}
              onChange={e => handleChange('id', e.target.value)}
              disabled={isEditing}
              placeholder="e.g., user_preferences"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.id ? 'border-red-300' : 'border-gray-300'
              } ${isEditing ? 'bg-gray-100' : ''}`}
            />
            {errors.id && <p className="mt-1 text-sm text-red-600">{errors.id}</p>}
            {isEditing && (
              <p className="mt-1 text-xs text-gray-500">ID cannot be changed after creation</p>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="e.g., User Preferences"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Section Type *
            </label>
            <select
              value={formData.type}
              onChange={e => handleChange('type', e.target.value as SectionType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {SECTION_TYPES.map(type => (
                <option key={type} value={type}>
                  {SECTION_TYPE_INFO[type].label} - {SECTION_TYPE_INFO[type].description}
                </option>
              ))}
            </select>
          </div>

          {/* Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Source *
            </label>
            <select
              value={formData.source}
              onChange={e => handleChange('source', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.source ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">-- Select a source --</option>
              {AVAILABLE_SOURCES[formData.type]?.map(source => (
                <option key={source.value} value={source.value}>
                  {source.label} ({source.value})
                </option>
              ))}
            </select>
            {errors.source && <p className="mt-1 text-sm text-red-600">{errors.source}</p>}
            {formData.source && (
              <p className="mt-1 text-xs text-gray-500">
                {AVAILABLE_SOURCES[formData.type]?.find(s => s.value === formData.source)?.description}
              </p>
            )}
          </div>

          {/* Order - Read-only, controlled by drag-and-drop */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.order}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
              />
              <p className="mt-1 text-xs text-gray-500">
                Order is controlled by drag-and-drop in the section list
              </p>
            </div>
          )}

          {/* Wrap Tag */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Wrap Tag (optional)
            </label>
            <input
              type="text"
              value={formData.wrap_tag || ''}
              onChange={e => handleChange('wrap_tag', e.target.value || null)}
              placeholder="e.g., memories"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Content will be wrapped in &lt;tag&gt;...&lt;/tag&gt;
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={formData.description || ''}
              onChange={e => handleChange('description', e.target.value || null)}
              rows={2}
              placeholder="Describe what this section contains..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Enabled */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="enabled"
              checked={formData.enabled}
              onChange={e => handleChange('enabled', e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="enabled" className="text-sm text-gray-700">
              Enabled (include in prompt)
            </label>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
            {isEditing ? 'Update' : 'Add'} Section
          </Button>
        </div>
      </div>
    </div>
  );
}
